import { useState, forwardRef, useImperativeHandle, useEffect } from "react";
import { Alert, Box, Button, Checkbox, Divider, FormControlLabel, Grid, Radio, Switch, TextField, Tooltip, Typography, useTheme } from "@mui/material";
import { EmailProvider, newEmailProvider } from "../Email";

interface EmailProviderFormProps {
  item: EmailProvider;
  error?: string;
}

export interface EmailProviderFormRef {
  valid: () => boolean;
  getItem: () => EmailProvider;
}

export const EmailProviderForm = forwardRef((props: EmailProviderFormProps, ref) => {
  const { error } = props;
  const [item, setItem] = useState<EmailProvider>(newEmailProvider());
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    setItem(props.item);
  }, [props.item]);

  const [touched, setTouched] = useState<{
    base: Record<string, boolean>;
  }>({
    base: {},
  });

  const valid = () => {
    let hasErrors = false;

    // Validate all base fields
    for (const key of Object.keys(item) as (keyof EmailProvider)[]) {
      if (typeof item[key] !== "object" && validateBaseField(key)) {
        hasErrors = true;
      }
    }

    setSubmitted(true);
    return !hasErrors;
  }

  const getItem = () => {
    return item;
  }

  // Expose the valid function to the parent, and a method to get the whole item object
  useImperativeHandle(ref, () => ({
    valid,
    getItem,
  }));

  const validateBaseField = (field: keyof EmailProvider): string => {
    if (field === "name") {
      if (!item.name?.trim()) return "Navn kan ikke være tom";
    }
    return ""; // No error
  };

  const handleBaseBlur = (field: keyof EmailProvider) => {
    setTouched((prev) => ({
      ...prev,
      base: { ...prev.base, [field]: true },
    }));
  };

  const handleIntegerChange = (field: keyof EmailProvider, value: any) => {
    value = parseInt(value, 10);
    setItem((prev) => ({ ...prev, [field]: value }));
  }

  const handleBaseChange = (field: keyof EmailProvider, value: any) => {  
    setItem((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <form>
      <Grid container spacing={2}
        sx={{ justifyContent: "center", alignItems: "center" }}
      >
        {error && <Alert sx={{ width: '100%' }} severity="error">{error}</Alert>}

        <Grid item xs={12}>
          <TextField
            label="Navn"
            required
            fullWidth
            value={item.name}
            onChange={(e) => handleBaseChange("name", e.target.value)}
            variant="outlined"
            error={validateBaseField("name") !== "" && (submitted || touched.base.name)}
            helperText={(submitted || touched.base.name) ? validateBaseField("name") : ""}
            onBlur={() => handleBaseBlur("name")}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Smtp Server"
            fullWidth
            value={item.smtp_server}
            onChange={(e) => handleBaseChange("smtp_server", e.target.value)}
            variant="outlined"
            error={validateBaseField("smtp_server") !== "" && (submitted || touched.base.smtp_server)}
            helperText={(submitted || touched.base.smtp_server) ? validateBaseField("smtp_server") : ""}
            onBlur={() => handleBaseBlur("smtp_server")}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Smtp Port"
            fullWidth
            value={item.smtp_port ?? ''}
            onChange={(e) => handleIntegerChange("smtp_port", e.target.value)}
            variant="outlined"
            type={"number"}
            error={validateBaseField("smtp_port") !== "" && (submitted || touched.base.smtp_port)}
            helperText={(submitted || touched.base.smtp_port) ? validateBaseField("smtp_port") : ""}
            onBlur={() => handleBaseBlur("smtp_port")}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="encryption_method"
            fullWidth
            value={item.encryption_method}
            onChange={(e) => handleBaseChange("encryption_method", e.target.value)}
            variant="outlined"
            error={validateBaseField("encryption_method") !== "" && (submitted || touched.base.encryption_method)}
            helperText={(submitted || touched.base.encryption_method) ? validateBaseField("encryption_method") : ""}
            onBlur={() => handleBaseBlur("encryption_method")}
          />
        </Grid>
      </Grid>
    </form>
  )
});